import React, { useState, useEffect } from 'react';
import { withStyles, Paper, CircularProgress } from '@material-ui/core';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import History from '../../../History';
import { parseURL } from "../../sc-utilities";
import * as actions from './store/externalSystemSettingActions';
import { BackButton, isValidMapping, isValidRegex } from '../../sc-utilities';
import FormStep1Database from './FormSteps/FormStep1Database';
import FormStep1Json from './FormSteps/FormStep1Json';
import FormStep1Kloudless from './FormSteps/FormStep1Kloudless';
import FormStep1Service from './FormSteps/FormStep1Service';
import FormStep1ServiceNow from './FormSteps/FormStep1ServiceNow';
import FormStep1Makor from './FormSteps/FormStep1Makor';
import FormStep1StoreExternalData from './FormSteps/FormStep1StoreExternalData';

import { CancelToken } from 'axios';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    }
});

const ExternalSystemSettingEdit = props => {
    const { activeCustomerID, translations, classes, onEditExternalSystemSetting, onFetchExternalSystemSetting, onFetchExternalSystemSettingInit, externalSystemSetting } = props;
    const [external, setExternal] = useState();
    const [snapshot, setSnapshot] = useState(null);
    let searchParams = parseURL(window.location.search);

    const submitHandler = async data => {
        let result = await onEditExternalSystemSetting({ ...data, ActiveCustomerID: activeCustomerID, ExternalSystemSettingID: searchParams.externalSystemSettingID });
        if (result) {
            History.goBack();
        }
    }

    useEffect(() => {
        const requestToken = CancelToken.source();
        const fetchData = async () => {
            await onFetchExternalSystemSetting({ ExternalSystemSettingID: searchParams.externalSystemSettingID, ActiveCustomerID: activeCustomerID }, { cancelToken: requestToken.token })
        }
        fetchData();

        return () => {
            requestToken.cancel('Operation canceled by the user.');
            onFetchExternalSystemSettingInit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (externalSystemSetting) {
            setExternal(JSON.parse(externalSystemSetting.Settings))
        }
    }, [externalSystemSetting]);

    let initialValues = {
        Type: externalSystemSetting?.Type ?? '',
        Name: externalSystemSetting?.Name ?? '',
        Direction: externalSystemSetting?.Direction ?? '',
        Active: externalSystemSetting?.Active === 1 ? true : false,
        ServiceType: externalSystemSetting?.ServiceType ?? '',
        Hostname: external?.hostname ?? external?.Hostname ?? '',
        Protocol: external?.protocol ? `${external.protocol}:` : external?.Protocol ? `${external.Protocol}` : 'http',
        AuthType: external?.authType ?? external?.AuthType ?? '',
        Port: external?.port ?? external?.Port ?? '',
        UseExternalTypicalProblems: external?.useExternalTypicalProblems ?? external?.UseExternalTypicalProblems ?? false,
        DatabaseName: external?.databaseName ?? external?.DatabaseName ?? '',
        TableName: external?.tableName ?? external?.TableName ?? '',
        Username: external?.username ?? external?.Username ?? '',
        Password: external?.password ?? external?.Password ?? '',
        CustomMapping: external?.CustomMapping ?? false,
        TableMapping: external?.TableMapping ? external?.TableMapping : external?.CustomMapping ? '{"PCProduct": {}, "MobileProduct": {}, "StorageProduct": {}, "DisplayProduct": {}}' : '',
        BearerToken: external?.BearerToken ?? external?.bearerToken ?? '',
        ApiKey: external?.ApiKey ?? external?.ApiKey ?? '',
        InstanceID: external?.InstanceID ?? external?.instanceID ?? '',
        ClientID: external?.ClientID ?? external?.clientID ?? '',
        ClientSecret: external?.ClientSecret ?? external?.clientSecret ?? '',
        ClientIDHeaderName: external?.ClientIDHeaderName ?? '',
        ClientIDHeaderValue: external?.ClientIDHeaderValue ?? '',
        ClientSecretHeaderName: external?.ClientSecretHeaderName ?? '',
        ClientSecretHeaderValue: external?.ClientSecretHeaderValue ?? '',
        Path: external?.Path ?? external?.path ?? '',
        TriggerOnSessionStart: external?.TriggerOnSessionStart ?? true,
        TriggerOnModifiedWeb: external?.TriggerOnModifiedWeb ?? true,
        TriggerOnModifiedProcessing: external?.TriggerOnModifiedProcessing ?? true,
        TriggerOnErasureProgress: external?.TriggerOnErasureProgress ?? false,
        TriggerOnSessionEnd: external?.TriggerOnSessionEnd ?? true,
        TriggerOnReportGenerated: external?.TriggerOnReportGenerated ?? true,
        SendReportMethod: external?.SendReportMethod ?? 'base64pdf',
        SendReportOnSuccessfulErasure: external?.SendReportOnSuccessfulErasure ?? false,
        SendReportOnFailedErasure: external?.SendReportOnFailedErasure ?? false,
        SendReportOnNoErasure: external?.SendReportOnNoErasure ?? false,
        OutputFormat: external?.OutputFormat ?? 'json',
        ProvideExternalIDEnabled: {
            PCProduct: external?.ExternalSystemBehaviour?.ProvideExternalID?.PCProduct?.Enabled ?? false,
            MobileProduct: external?.ExternalSystemBehaviour?.ProvideExternalID?.MobileProduct?.Enabled ?? false,
            StorageProduct: external?.ExternalSystemBehaviour?.ProvideExternalID?.StorageProduct?.Enabled ?? false,
        },
        ProvideExternalIDRegexp: external?.ExternalSystemBehaviour?.ProvideExternalID?.Regexp ?? '',
        ProvideExternalIDDescription: external?.ExternalSystemBehaviour?.ProvideExternalID?.Description ?? '',
        ProvideExternalIDName: external?.ExternalSystemBehaviour?.ProvideExternalID?.Name ?? '',
        AutostartErasureOnMethodEnabled: external?.ExternalSystemBehaviour?.AutostartErasureOnMethod?.Enabled ?? false,
        ConfirmDataExportEnabled: external?.ExternalSystemBehaviour?.ConfirmDataExport?.Enabled ?? false,
        ConfirmDataExportMessage: external?.ExternalSystemBehaviour?.ConfirmDataExport?.Message ?? '',
        SendErasureReportEndpoint: external?.SendErasureReportEndpoint ?? '',
        SendPerformedDiagnoseCasesOnly: external?.SendPerformedDiagnoseCasesOnly ?? true,
        ErasureMethodMapping: external?.ErasureMethodMapping ?? '{}',
        TriggerOnErasureFinished: external?.TriggerOnErasureFinished ?? false,
        TriggerOnAnalyzeFinished: external?.TriggerOnAnalyzeFinished ?? false,
        TriggerOnOperatorWorkflowDone: external?.TriggerOnOperatorWorkflowDone ?? false
    };

    useEffect(() => {
        if (external) {
            setSnapshot(initialValues)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [external]);

    let formSettings = {
        Validation:
        {
            Name: Yup.string().required(),
            TriggerOnSessionStart: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnModifiedWeb: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnModifiedProcessing: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnSessionEnd: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnErasureFinished: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnAnalyzeFinished: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnOperatorWorkflowDone: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            Hostname: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'service' || val === 'json' || val === 'makor',
                then: Yup.string().required()
            }),
            Port: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'service' || val === 'json' || val === 'makor',
                then: Yup.string().required()
            }),
            DatabaseName: Yup.string().when('Type', {
                is: val => val === 'database',
                then: Yup.string().required()
            }),
            TableName: Yup.string().when('Type', {
                is: val => val === 'database',
                then: Yup.string().required()
            }),
            InstanceID: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            ClientID: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            ClientSecret: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            BearerToken: Yup.string().when('Type', {
                is: val => val === 'kloudless',
                then: Yup.string().required()
            }),
            Path: Yup.string().when('Type', {
                is: val => val === 'json' || val === 'makor',
                then: Yup.string().required()
            }),
            CustomMapping: Yup.boolean().when('Type', {
                is: val => val === 'makor',
                then: Yup.boolean().required()
            }),
            TableMapping: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'json' || val === 'kloudless' || val === 'servicenow',
                then: Yup.string().required().test('jsonTest', 'Not a valid JSON', value => isValidMapping(value))
            }),
            ProvideExternalIDRegexp: Yup.string().when('Type', {
                is: val => val === 'json',
                then: Yup.string().when(['ProvideExternalIDEnabled.PCProduct', 'ProvideExternalIDEnabled.MobileProduct', 'ProvideExternalIDEnabled.StorageProduct'], {
                    is: (pcProduct, mobileProduct, storageProduct) => pcProduct || mobileProduct || storageProduct,
                    then: Yup.string().test('regexTest', 'Not a valid Regular Expression', value => isValidRegex(value))
                })
            }),
            Username: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'servicenow',
                then: Yup.string().required()
            }),
            Password: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'servicenow',
                then: Yup.string().required()
            }),
        },
        ButtonSubmit: {
            Title: translations['save'] || 'SAVE',
            Color: 'primary'
        },
    };

    const formProps={
        handleSubmit: submitHandler, 
        initialValues: snapshot, 
        translations, 
        formSettings,
        edit: true
    }

    return <Paper className={classes.root}>
        {BackButton(translations)}
        {externalSystemSetting && external && snapshot ? (
            <>
                {externalSystemSetting.Type === 'service' ? (
                    <FormStep1Service {...formProps} />
                ) : externalSystemSetting.Type === 'database' ? (
                    <FormStep1Database {...formProps} />
                ) :  externalSystemSetting.Type === 'json' ? (
                    <FormStep1Json {...formProps} />
                ) :  externalSystemSetting.Type === 'servicenow' ? (
                    <FormStep1ServiceNow {...formProps} />
                ) :  externalSystemSetting.Type === 'kloudless' ? (
                    <FormStep1Kloudless {...formProps} />
                ) :  externalSystemSetting.Type === 'makor' ? (
                    <FormStep1Makor {...formProps} />
                ) :  externalSystemSetting.Type === 'store_external_data' ? (
                    <FormStep1StoreExternalData {...formProps} />
                ) : null}
            </>
        ) : <CircularProgress
            variant="determinate"
            color="primary"
            size={28}
        />}
    </Paper >
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        externalSystemSetting: state.externalSystemSettings.externalSystemSetting.loaded
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onEditExternalSystemSetting: setting => dispatch(actions.editExternalSystemSetting(setting)),
        onFetchExternalSystemSetting: (setting, option) => dispatch(actions.loadExternalSystemSetting(setting, option)),
        onFetchExternalSystemSettingInit: setting => dispatch(actions.loadExternalSystemSettingInit(setting)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ExternalSystemSettingEdit));
